import React, { useEffect, useState } from 'react';
import { View } from 'react-native';
import { useQuery, useMutation } from '@apollo/client';
import DataTable from '@preflighttech/preflight-tables';
import styled from 'styled-components/native';

import { Button, ButtonGroup } from 'app/src/elements/buttons';
import DataState from 'app/src/components/DataState';
import {
  MainContainer,
  ContentContainer,
  PageTitle,
} from 'app/src/elements';
import minimalTable from 'app/src/elements/minimalTable';
import { Picker } from 'app/src/elements/Picker';
import { StyledPicker, StyledTextInput } from 'app/src/elements/inputs';
import { Text, Subtitle } from 'app/src/styles';
import { currency } from 'app/src/utils/stringHelpers';

import {
  PADDLE_SERVICE_PAYMENTS,
  CREATE_PADDLE_PAYMENTS,
  CREDIT_PADDLE_PAYMENTS,
} from './queries';
import Payment from './show';

const Option = styled(View)`
  flex-direction: row;
  gap: 10;
  align-items: baseline;
  margin-top: 10px;
`;

export const Section = styled(View)`
  margin-top: 20px;
`;

export const saleAssetColumns = props => {
  const { saleAssetAmounts, setSaleAssetAmounts } = props;

  return [
    {
      key: 'paddleService',
      label: 'Paddle Service',
      sort: 'asc',
      value: ({ value }) => value.name,
    },
    {
      key: 'lotNumber',
      label: 'Lot #',
    },
    {
      key: 'paddleNumber',
      label: 'Paddle #',
    },
    {
      key: 'stockNumber',
      label: 'Stock #',
      value: ({ entry }) => entry.asset.stockNumber,
    },
    {
      key: 'soldOrIfPrice',
      label: 'Sale Price',
      cellStyle: { textAlign: 'right' },
      content: ({ value }) => currency(value),
    },
    {
      key: 'paddlePaymentAmount',
      label: 'Payment Amount',
      cellStyle: { textAlign: 'right' },
      content: ({ value }) => currency(value),
    },
    {
      key: 'add',
      label: 'Add to Payment?',
      sort: 'prevent',
      content: ({ entry }) => {
        const isChecked = !!(saleAssetAmounts.find(
          saleAsset => saleAsset.id === entry.id
        ));

        return (
          <input
            type="checkbox"
            checked={isChecked}
            onChange={() => {
              if (isChecked) {
                setSaleAssetAmounts(
                  [...saleAssetAmounts.filter(item => item.id !== entry.id)]
                );
              } else {
                setSaleAssetAmounts([ ...saleAssetAmounts, entry ]);
              }
            }}
          />
        );
      }
    },
  ];
};

const PaddleServicePayments = () => {
  const [saleId, setSaleId] = useState('');
  const [saleAssetAmounts, setSaleAssetAmounts] = useState([]);
  const [creditAmounts, setCreditAmounts] = useState([]);
  const [paddleServiceInputs, setPaddleServiceInputs] = useState({});
  const [creditPaddleServiceInputs, setCreditPaddleServiceInputs] =
    useState({});

  const { data, loading, error, refetch } = useQuery(PADDLE_SERVICE_PAYMENTS, {
    variables: { saleId: (saleId || null) }
  });

  const handleMutationCompleted = () => {
    refetch();
    setSaleAssetAmounts([]);
    setCreditAmounts([]);
    setPaddleServiceInputs({});
    window.scrollTo(0, 0);
  };

  const [createPaddlePayments] = useMutation(CREATE_PADDLE_PAYMENTS, {
    onCompleted: handleMutationCompleted,
  });

  const [creditPaddlePayments] = useMutation(CREDIT_PADDLE_PAYMENTS, {
    onCompleted: handleMutationCompleted,
  });

  useEffect(() => {
    if (data && '' === saleId) {
      setSaleId(data.paddleServicePayments.sale.id);
    }
  }, [data]);

  if (loading || !data) return <DataState.Loading />;
  if (error) return <DataState.Error error={error} />;

  const { sale, sales, txns, pendingSaleAssets } = data.paddleServicePayments;

  const counts = {};
  const paddleServices = {};

  saleAssetAmounts.forEach(saleAsset => {
    if (!counts[saleAsset.paddleService.id]) {
      counts[saleAsset.paddleService.id] = {
        count: 0,
        amount: 0.0,
        saleAssetIds: [],
      };
    }

    counts[saleAsset.paddleService.id].count =
      counts[saleAsset.paddleService.id].count + 1;

    counts[saleAsset.paddleService.id].amount =
      counts[saleAsset.paddleService.id].amount +
      parseFloat(saleAsset.paddlePaymentAmount);

    counts[saleAsset.paddleService.id].saleAssetIds.push(saleAsset.id);

    paddleServices[saleAsset.paddleService.id] = saleAsset.paddleService.name;
  });

  const creditCounts = {};
  const creditPaddleServices = {};

  creditAmounts.forEach(txnLine => {
    const { saleAsset } = txnLine;
    if (!creditCounts[saleAsset.paddleService.id]) {
      creditCounts[saleAsset.paddleService.id] = {
        count: 0,
        amount: 0.0,
        txnLineIds: [],
      };
    }

    creditCounts[saleAsset.paddleService.id].count =
      creditCounts[saleAsset.paddleService.id].count + 1;

    creditCounts[saleAsset.paddleService.id].amount =
      creditCounts[saleAsset.paddleService.id].amount +
      parseFloat(txnLine.debitLessCredit);

    creditCounts[saleAsset.paddleService.id].txnLineIds.push(txnLine.id);

    creditPaddleServices[saleAsset.paddleService.id] =
      saleAsset.paddleService.name;
  });

  const createButtonLabel = Object.keys(counts).length > 1 ?
    'Create Payments' : 'Create Payment';

  const handleCreate = () => {
    Object.keys(counts).forEach(paddleServiceId => {
      createPaddlePayments({
        variables: {
          saleAssetIds: counts[paddleServiceId].saleAssetIds,
          ...(paddleServiceInputs[paddleServiceId]),
        }
      });
    });
  };

  const handleCreateCredits = () => {
    Object.keys(creditCounts).forEach(paddleServiceId => {
      creditPaddlePayments({
        variables: {
          txnLineIds: creditCounts[paddleServiceId].txnLineIds,
          ...(creditPaddleServiceInputs[paddleServiceId]),
        }
      });
    });
  };

  return (
    <MainContainer>
      <ContentContainer>
        <PageTitle>Paddle Service Payments</PageTitle>

        <Option>
          <Text>Sale:</Text>

          <StyledPicker
            selectedValue={saleId}
            onValueChange={setSaleId}
          >
            {sales.map(({ id, name }) => (
              <Picker.Item key={id} value={id} label={name} />
            ))}
          </StyledPicker>
        </Option>

        {txns.length > 0 && (
          <Section>
            <Subtitle>Paddle Service Payment Transactions</Subtitle>
            {txns.map(txn => (
              <Payment
                key={txn.id}
                txn={txn}
                creditAmounts={creditAmounts}
                setCreditAmounts={setCreditAmounts}
              />
            ))}
          </Section>
        )}

        {Object.keys(creditCounts).length > 0 && (
          <View>
            {Object.keys(creditCounts).map(paddleServiceId => (
              <Option key={paddleServiceId}>
                <Text>
                  {creditPaddleServices[paddleServiceId]}:{' '}
                  {creditCounts[paddleServiceId].count} Assets{' - '}
                  {currency(creditCounts[paddleServiceId].amount)}
                </Text>
                <Text>| Reference (optional):</Text>
                <StyledTextInput
                  value={
                    creditPaddleServiceInputs[paddleServiceId]?.reference || ''
                  }
                  onChangeText={value => {
                    const inputs = {...creditPaddleServiceInputs};
                    inputs[paddleServiceId] = {
                      ...(inputs[paddleServiceId] || {}), reference: value
                    };
                    setCreditPaddleServiceInputs(inputs);
                  }}
                />
              </Option>
            ))}

            <ButtonGroup>
              <Button label="Create Credits" onPress={handleCreateCredits} />
            </ButtonGroup>
          </View>
        )}

        <Section>
          <Subtitle>Pending Paddle Service Payment</Subtitle>

          <DataTable.Simple
            data={pendingSaleAssets}
            columns={
              saleAssetColumns({
                saleAssetAmounts,
                setSaleAssetAmounts,
              })
            }
            component={minimalTable}
            pageLength="All"
          />

          {Object.keys(counts).map(paddleServiceId => (
            <Option key={paddleServiceId}>
              <Text>
                {paddleServices[paddleServiceId]}:{' '}
                {counts[paddleServiceId].count} Assets{' - '}
                {currency(counts[paddleServiceId].amount)}
              </Text>
              <Text>| Invoice # (optional):</Text>
              <StyledTextInput
                value={
                  paddleServiceInputs[paddleServiceId]?.reference || ''
                }
                onChangeText={value => {
                  const inputs = {...paddleServiceInputs};
                  inputs[paddleServiceId] = {
                    ...(inputs[paddleServiceId] || {}), reference: value
                  };
                  setPaddleServiceInputs(inputs);
                }}
              />
            </Option>
          ))}

          <ButtonGroup>
            <Button
              label={createButtonLabel}
              onPress={handleCreate}
              disabled={0 === Object.keys(counts).length}
            />
          </ButtonGroup>
        </Section>
      </ContentContainer>
    </MainContainer>
  );
};

export default PaddleServicePayments;
